import type { WatchPartyRecordingState } from '../../types/auth';
import type { LiveRecordingState } from '../../types/liveRecordings';

/**
 * CORE-1526 (QA FAIL 05.10, пп. 3/8) — что случилось с записью завершённой
 * сессии, одним ответом на всю страницу эфира.
 *
 * До этого страница знала о записи ровно один признак — есть ли `playbackUrl`,
 * — и третьего состояния у неё не было: ссылки нет, значит «обрабатывается».
 * Сорвавшаяся запись, запись, которую не вели, и запись месячной давности
 * получали один и тот же спиннер с обещанием «обычно 5–15 минут». В архиве та
 * же запись в это время честно называлась «Не записалась».
 *
 * Состояние записи в контракте страницы эфира (`LiveDetailResponse`) не
 * приходит вовсе — только `playbackUrl`. Поэтому исход собирается из двух
 * источников, в этом порядке:
 *
 *   1. известное состояние — его передаёт архив, открывая сессию карточкой
 *      («Открыть сессию и чат»), и там оно авторитетное;
 *   2. время — обещание обработки живёт ровно столько, сколько обработка
 *      может идти (бэкенд называет 5–15 минут, окно взято с запасом).
 *
 * Сервер, добавив `recordingState` в ответ эфира, заменит собой оба: тогда
 * `knownState` будет приходить всегда, и временная оценка останется только
 * страховкой для старых сборок.
 */
export type RecordingOutcome =
  /** Файл есть, его можно открыть. */
  | 'ready'
  /** Файл ещё собирается — ждать имеет смысл. */
  | 'processing'
  /** Запись велась, но файла не будет. */
  | 'failed'
  /** Файла нет и не ожидается: не записывали или он так и не появился. */
  | 'missing';

/**
 * Сколько ещё ждать файл после планового конца сессии.
 *
 * Бэкенд называет 5–15 минут (`api.ts`, финализация Agora). Час — запас на
 * длинную очередь кодировщика: ошибиться в сторону «ещё немного подождите»
 * безопаснее, чем сказать «записи нет» о файле, который вот-вот появится.
 * Тот же час стоит в анонсе комнаты (`RoomAnnouncementScreen`).
 */
export const RECORDING_PROCESSING_WINDOW_MS = 60 * 60 * 1000;

export interface RecordingOutcomeInput {
  /** Есть ли что открыть прямо сейчас — ссылка на файл или видео. */
  hasPlayback: boolean;
  /**
   * Состояние записи, если его кто-то знает: архив записей (строчными) или
   * комната (с заглавной). Нет — исход считается по времени.
   */
  knownState?: LiveRecordingState | WatchPartyRecordingState | null;
  startDateTimeUtc?: string | null;
  /** Плановая длительность: конца сессии в контракте страницы нет. */
  durationMinutes?: number | null;
  now?: number;
}

type NormalizedState = LiveRecordingState | null;

function normalizeState(state: RecordingOutcomeInput['knownState']): NormalizedState {
  if (!state) return null;
  const lower = String(state).toLowerCase();
  switch (lower) {
    case 'none':
    case 'recording':
    case 'processing':
    case 'ready':
    case 'failed':
      return lower;
    default:
      return null;
  }
}

export function resolveRecordingOutcome(input: RecordingOutcomeInput): RecordingOutcome {
  // Открывать есть что — остальное неважно: состояние могло устареть, файл
  // уже здесь.
  if (input.hasPlayback) return 'ready';

  const state = normalizeState(input.knownState);
  if (state === 'failed') return 'failed';
  if (state === 'none') return 'missing';
  if (state === 'recording' || state === 'processing') return 'processing';
  // `ready` без ссылки — открывать всё равно нечего, и называть это готовым
  // значит предложить несуществующий файл. Решает время, как и без состояния.

  const startedMs = input.startDateTimeUtc ? Date.parse(input.startDateTimeUtc) : NaN;
  // Без даты начала судить не по чему: оставляем прежнее поведение страницы,
  // а не выдумываем отказ.
  if (Number.isNaN(startedMs)) return 'processing';

  const plannedMs = input.durationMinutes != null && input.durationMinutes > 0
    ? input.durationMinutes * 60_000
    : 0;
  const now = input.now ?? Date.now();
  return now <= startedMs + plannedMs + RECORDING_PROCESSING_WINDOW_MS ? 'processing' : 'missing';
}

export interface OwnerRecordingCopy {
  title: string;
  body: string;
  /** Имеет ли смысл кнопка «Обновить»: ждать нечего — и обновлять нечего. */
  canRefresh: boolean;
}

/**
 * Слова владельцу о его записи — те же, что в архиве «Записи Live-сессий»
 * (`recordingsCopy.statusChip`, `FAILED_HINT`), чтобы одна и та же запись
 * называлась одинаково на карточке и на странице сессии.
 */
export function ownerRecordingCopy(
  outcome: RecordingOutcome,
  options: { isRoom?: boolean } = {},
): OwnerRecordingCopy {
  const event = options.isRoom ? 'практика' : 'Live-сессия';
  const eventStays = `Сама ${event} и её чат остаются здесь.`;
  switch (outcome) {
    case 'ready':
      return { title: 'Запись готова', body: 'Откройте, чтобы пересмотреть или поделиться ссылкой.', canRefresh: false };
    case 'processing':
      return { title: 'Запись обрабатывается', body: 'Обычно это занимает 5-15 минут.', canRefresh: true };
    case 'failed':
      // «Не записалась» — ровно тот же чип, что у карточки в архиве.
      return {
        title: 'Не записалась',
        body: `Запись велась, но файл не получился. ${eventStays}`,
        canRefresh: false,
      };
    case 'missing':
      // Почему файла нет, страница не знает: не записывали или он так и не
      // появился. Говорим то, что знаем, и не обещаем ожидание.
      return {
        title: 'Записи нет',
        body: `Файл так и не появился. ${eventStays}`,
        canRefresh: false,
      };
    default: {
      const unreachable: never = outcome;
      return unreachable;
    }
  }
}
